import ResponsiveTypography from '@/components/ui/responsive-typography';
import {Box, SxProps, Theme, Tooltip, useTheme} from '@mui/material';
import {
  extractEmailPart,
  getProperMailAddress
} from '@/components/matrix-chat/utils/helper-functions';
import {useCallback, useEffect, useMemo, useState} from 'react';
import UserOverview, {UserInOverview} from '@/components/user/overview';

import {useMatrixRoomContext} from '@/contexts/matrix/room';
import {useTranslation} from 'react-i18next';
import {getUserByMail} from '@/rest/user/get-user-by-mail';
import {useDispatch} from 'react-redux';
import {useAppSelector} from '@/redux-store/store';
import {setUserData} from '@/redux-store/slices/matrix/user-data';

interface SenderNameProps {
  showUserProfileByClick?: boolean;
  senderId: string;
  senderName?: string;
  additionalMarginBottom?: number;
  sx?: SxProps<Theme>;
}

const SenderName: React.FC<SenderNameProps> = ({
  showUserProfileByClick,
  senderName,
  senderId,
  additionalMarginBottom = 0,
  sx
}) => {
  const [userOverviewDialogOpen, setUserOverviewDialogOpen] = useState(false);
  const [selectedUserToOverview, setSelectedUserToOverview] =
    useState<UserInOverview | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const {handleOpenUserDialog, handleCloseUserDialog} = useMatrixRoomContext();

  const dispatch = useDispatch();

  const {t} = useTranslation();

  const theme = useTheme();

  const userMail = useMemo(() => {
    const extractedMail = extractEmailPart(senderId);
    if (extractedMail) {
      const userMail = getProperMailAddress(extractedMail);
      return userMail;
    }
  }, [senderId]);

  const userData = useAppSelector(
    (store) => store.matrixUser.userDataByEmail[userMail ?? '']
  );

  const userNameToShow = senderName || userName;

  const handleUserOverviewClick = useCallback(
    (userMail: string) => {
      getUserByMail(userMail)
        .then((fetchedUser) => {
          setSelectedUserToOverview(fetchedUser);
          setUserOverviewDialogOpen(true);

          handleOpenUserDialog();
        })
        .catch((error) => {
          console.error('Failed to fetch user', error);
        });
    },
    [handleOpenUserDialog]
  );

  const handleUserOverviewClose = useCallback(() => {
    setSelectedUserToOverview(null);
    setUserOverviewDialogOpen(false);
    handleCloseUserDialog();
  }, [handleCloseUserDialog]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (userMail && !userData) {
          const fetchedData = await getUserByMail(userMail);
          if (fetchedData) {
            dispatch(setUserData({email: userMail, data: fetchedData}));
            setUserName(fetchedData.name);
          }
        } else if (userData && userData.name) {
          setUserName(userData.name);
        }
      } catch (error) {
        console.error('Failed to get user data', error);
      }
    };

    fetchData();
  }, [userMail, userData, dispatch]);

  return (
    <>
      <Tooltip title={t('user_profile')}>
        <Box>
          <ResponsiveTypography
            onClick={() => {
              if (userMail) {
                handleUserOverviewClick(userMail);
              }
            }}
            smallAllSizes
            sx={{
              ...sx,
              fontWeight: 'bold',
              marginBottom: additionalMarginBottom,
              cursor: showUserProfileByClick ? 'pointer' : 'default',
              color: theme.palette.customColors?.messageTextOwn
            }}
          >
            {userNameToShow}
          </ResponsiveTypography>
        </Box>
      </Tooltip>

      {selectedUserToOverview && showUserProfileByClick && (
        <UserOverview
          userData={selectedUserToOverview}
          userViewDialogOpen={userOverviewDialogOpen}
          onClose={handleUserOverviewClose}
        />
      )}
    </>
  );
};

export default SenderName;
